import { useContext, useEffect, useState } from "react";
import { useArrivalLogsHttp } from "./hooks/useArrivalLogsHttp";
import { LoadingButton } from "@mui/lab";
import { LocalizationProvider } from '@mui/x-date-pickers'
import { Card, CardHeader, IconButton, CardContent, Stack, FormControl, RadioGroup, FormControlLabel, Radio, TextField, CardActions, Tooltip, Box, Typography, Checkbox } from "@mui/material";
import { SelectVillage } from "../../components/SelectVillage";
import { Asset } from "../../data/Asset";
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { Dashboard } from "../../enums/Dashboard";
import SearchIcon from '@mui/icons-material/Search';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from '@mui/x-date-pickers'
import { DataGrid, GridColDef, GridSortItem } from "@mui/x-data-grid";
import { bhpColor, dataGridSx } from "../../styles/bhpStyles";
import { formatDate, formatDateTime } from "../../utils";
import InfoIcon from '@mui/icons-material/Info';
import { ArrivalLogsPagination } from "./ArrivalLogsPagination";
import { ArrivalLogDto } from "./dtos/ArrivalLogDto";
import VspPortalContext from "../../store/VspPortalContext";
import { CheckHealth } from "../Maintenance/components/CheckHealth";


const columns: GridColDef[ ] = [ 

    {
        field: 'firstName',
        headerName: 'First Name',
        flex: 1
    },
    {
        field: 'surname',
        headerName: 'Last Name',
        flex: 1
    },
    {
        field: 'employeeNumber',
        headerName: 'Employee Id',
        flex: 1
    },
    {
        field: 'cardNumber',
        headerName: 'Card Number',
        flex: 1,
        renderCell: params => 
            <>
                {
                    params.row.cardNumber
                        &&   
                    <Tooltip title={ `Card Uuid: ${ params.row.cardUuid }` }>

                        <Stack direction="row">
                            <Typography>{ params.row.cardNumber }</Typography>
                            <InfoIcon sx={{ color: bhpColor, marginLeft: '7px' }} />
                        </Stack>

                    </Tooltip>
                }
            </>
    },
    {
        field: 'comments',
        headerName: 'Comments',
        flex: 3,
        renderCell: params => 
            <Tooltip title={<div style={{ fontSize: '18px' }}>{ params.row.comments }</div>}>
                <Typography>{ params.row.comments }</Typography>
            </Tooltip>
    },
    {
        field: 'firstNightInRoom',
        headerName: 'First Night',
        flex: 1,
        renderCell: ( params => formatDate( params.row.firstNightInRoom.substring( 0, 10 ) ) )
    },
    {
        field: 'lastNightInRoom',
        headerName: 'Last Night',
        flex: 1,
        renderCell: ( params => formatDate( params.row.lastNightInRoom.substring( 0, 10 ) ) )
    },
    {
        field: 'isKiosk',
        headerName: 'Is Kiosk',
        flex: 1,
        renderCell: params => <Checkbox checked={ params.row.isKiosk } disabled/>
    },
    {
        field: 'movement',
        headerName: 'Movement',
        flex: 1
    },
    {
        field: 'requestDateTime',
        headerName: 'Request Time',
        flex: 1,
        renderCell: ( params => formatDateTime( params.row.requestDateTime ) )
    },
    {
        field: 'roomNumber',
        headerName: 'Room Number',
        flex: 1
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 1
    },

];


const textFieldSx = {

    marginTop: '10px', 

};


export const ArrivalLogs: React.FC = ( ) => {

    const [ arrivalLog, setArrivalLog ] = useState< ArrivalLogDto >(  );
    const [ asset, setAsset ] = useState< Asset >( Asset.Waio );
    const [ canSearch, setCanSearch ] = useState< boolean >( false );
    const [ cardNumber, setCardNumber ] = useState<string>( '' );
    const [ dateFrom, setDateFrom ] = useState< string  >( );
    const [ dateTo, setDateTo ] = useState< string  >( );
    const [ employeeId, setEmployeeId ] = useState<string>( '' );
    const [ firstName, setFirstName ] = useState<string>( '' );
    const [ isLoaded, setIsLoaded ] = useState< boolean >( false );
    const [ isLoading, setIsLoading ] = useState< boolean >( false );
    const [ isPaginationInitialized, setIsPaginationInitialized ] = useState< boolean >( false );
    const [ lastName, setLastName ] = useState<string>( '' );
    const [ roomNumber, setRoomNumber ] = useState<string>( '' );
    const [ village, setVillage ] = useState< string >( '' );
    
    const { getArrivalLog } = useArrivalLogsHttp( );

    const { state } = useContext( VspPortalContext );

    useEffect( ( ) => {

        setCanSearch( false );
        
        if( !village ) {
            
            return;

        }

        if( firstName && firstName.length < 3) {

            return;

        }

        if( lastName && lastName.length < 3) {

            return;

        }

        if( cardNumber || dateFrom || dateTo || employeeId || firstName || lastName || roomNumber ) {

            setCanSearch( true );

        }
        
    }, [ cardNumber, dateFrom, dateTo, employeeId, firstName, lastName, roomNumber, village ]);

    useEffect( () => {

        if( !isPaginationInitialized ){

            setIsPaginationInitialized( true );
            return;

        }

        const timer = setTimeout( ( ) =>  fetchArrivalLogs( ), 1000); 
     
        return () => { 
     
           clearTimeout( timer ); 
     
        };

    }, [ state.arrivalLogsPageNumber, state.arrivalLogsPageSize, state.arrivalLogsSorting ]);


    const handleAssetChange = ( event: React.ChangeEvent< HTMLInputElement > ) => { 
        
        setVillage( '' );
        setAsset( ( event.target as HTMLInputElement ).value as Asset ); 

    };
    

    const handleCardNumberChange = ( event: React.ChangeEvent<HTMLInputElement> ) => setCardNumber( event.target.value.trim( ) );


    const handleDateFromChange = ( date: Dayjs | null ) => {

        if( !date ){
            
            return;

        }

        const day = date.date( ) < 10 ? `0${ date.date( ) }` : `${ date.date( ) }`;
        const month = date.month( ) + 1 < 10 ? `0${ date.month( ) + 1 }` : `${ date.month( ) + 1 }`;
        const year = date.year( );
        const formattedDate = `${ year }-${ month }-${ day }`;
        setDateFrom( formattedDate ); 
        
    };


    const handleDateToChange = ( date: Dayjs | null ) => {

        if( !date ){
            
            return;

        }

        const day = date.date( ) < 10 ? `0${ date.date( ) }` : `${ date.date( ) }`;
        const month = date.month( ) + 1 < 10 ? `0${ date.month( ) + 1 }` : `${ date.month( ) + 1 }`;
        const year = date.year( );
        const formattedDate = `${ year }-${ month }-${ day }`;

        setDateTo( formattedDate ); 
        
    };


    const handleEmployeeIdChange = ( event: React.ChangeEvent<HTMLInputElement> ) => setEmployeeId( event.target.value.trim( ) );
    

    const handleFirstNameChange = ( event: React.ChangeEvent<HTMLInputElement> ) => setFirstName( event.target.value.trimStart( ) );


    const handleLastNameChange = ( event: React.ChangeEvent<HTMLInputElement> ) => setLastName( event.target.value.trimStart( ) );


    const handleResetClick = ( ) => {

        setVillage( '' );
        setAsset( Asset.Waio );
        setCanSearch( false );
        setCardNumber( '' );
        setDateFrom( undefined );
        setDateTo( undefined );
        setEmployeeId( '' );
        setFirstName( '' );
        setLastName( '' );
        setRoomNumber( '' );
        setArrivalLog( undefined );
        setIsLoaded( false );

    };


    const handleRoomNumberChange = ( event: React.ChangeEvent<HTMLInputElement> ) => setRoomNumber( event.target.value.trimStart( ) );


    const handleSearchClick = ( ) => fetchArrivalLogs( );


    const handleSelectVillage = ( villageCode: string ) => setVillage( villageCode );
    

    const fetchArrivalLogs = async( ) => {

        setIsLoading( true );

        const pageNumber = state.arrivalLogsPageNumber;
        const pageSize = state.arrivalLogsPageSize;
        const sorting = state.arrivalLogsSorting;

        const formattedDateFrom =  dateFrom ? `${ dateFrom }T00:00:00` : undefined;
        const formattedDateTo = dateTo ? `${ dateTo }T00:00:00` : undefined;

        const arrivalLog = await getArrivalLog( 
        
            cardNumber,
            employeeId, 
            firstName, 
            formattedDateFrom, 
            pageNumber,
            pageSize,
            roomNumber, 
            sorting,
            lastName, 
            formattedDateTo,
            village

        );

        setIsLoading( false );
        setIsLoaded( true );

        if( arrivalLog ) {
            
            setArrivalLog( arrivalLog );

        } else {
            
            setArrivalLog( undefined );

        }

    };


    return(
        
        <> 
        
            <CheckHealth />
            
            <Stack justifyContent="center" spacing={ 3 } sx={{ alignItems: "center", marginTop: "20px" }}>
            
                <Card sx={{ boxShadow: 5, width: '500px' }}>

                    <CardHeader 
                        sx={{ fontWeight: '600', fontSize: '20px', paddingTop: '5px', paddingBottom: '0' }}
                        title="Search Parameters"
                        avatar={
                            <PersonSearchIcon />
                        }
                        action={
                            <Tooltip title="Reset">
                                <IconButton onClick={ handleResetClick }>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    />

                    <CardContent>
                        
                        <Stack gap={0.5}>
                            
                            <Stack direction="row" alignItems="center">
                            
                                <FormControl 
                                    sx={{ marginRight: '10px' }}
                                    disabled={ isLoading }
                                >
                                    {/* <FormLabel>Asset</FormLabel> */}
                                    <RadioGroup 
                                        value={ asset }
                                        onChange={ handleAssetChange }
                                    >
                                        
                                        <FormControlLabel value={ Asset.Waio } control={<Radio />} label={ Asset.Waio } sx={{ marginBottom: '-5px', marginTop: '-5px' }}/>
                                        <FormControlLabel value={ Asset.Niw } control={<Radio />} label={ Asset.Niw } sx={{ marginBottom: '-5px', marginTop: '-5px' }}/>

                                    </RadioGroup>
                                </FormControl>
                                
                                <SelectVillage asset={ asset } dashboard={ Dashboard.ArrivalLogs } onSelectVillage={ handleSelectVillage } village={ village }/>
                                
                            </Stack>

                            <Stack direction="row" alignItems="center">
                                
                                <TextField
                                    sx={ textFieldSx } 
                                    label="First Name" 
                                    variant="outlined"
                                    value={ firstName }
                                    onChange={ handleFirstNameChange }
                                    disabled={ isLoading }
                                />

                                <TextField 
                                    sx={{ ...textFieldSx, marginLeft: '10px', width: '245px' }} 
                                    label="Last Name" 
                                    variant="outlined"
                                    value={ lastName }
                                    onChange={ handleLastNameChange }
                                    disabled={ isLoading} 
                                />

                            </Stack>

                            <TextField 
                                sx={ textFieldSx } 
                                label="Employee ID" 
                                variant="outlined"
                                value={ employeeId }
                                onChange={ handleEmployeeIdChange }
                                disabled={ isLoading }
                            />

                            <TextField 
                                sx={ textFieldSx } 
                                label="Card Number" 
                                variant="outlined"
                                value={ cardNumber }
                                onChange={ handleCardNumberChange }
                                disabled={ isLoading }
                            />

                            <TextField 
                                sx={ textFieldSx } 
                                label="Room Number" 
                                variant="outlined"
                                value={ roomNumber }
                                onChange={ handleRoomNumberChange }
                                disabled={ isLoading }
                            />

                            <LocalizationProvider dateAdapter={ AdapterDayjs } adapterLocale="en-au">
                            
                                <Stack direction="row">

                                    <DatePicker 
                                        sx={{ marginTop: '5px' }}
                                        label="Date From" 
                                        value = { dateFrom ? dayjs( dateFrom ) : null }
                                        onChange={ handleDateFromChange }
                                        maxDate = { dateTo ? dayjs( dateTo ) : undefined }
                                    />

                                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px', marginRight: '10px', fontWeight: 400, fontSize: 30, color: 'rgb(28, 32, 37)' }}>-</div>

                                    <DatePicker 
                                        disabled={ !dateFrom } 
                                        sx={{ marginTop: '5px' }}
                                        label="Date To" 
                                        value = { dateTo ? dayjs( dateTo ) : null }
                                        onChange={ handleDateToChange }
                                        minDate={ dayjs( dateFrom ) }
                                    />

                                </Stack>

                            </LocalizationProvider>

                        </Stack>

                    </CardContent>

                    <CardActions
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end"
                        }}
                    >

                        <LoadingButton
                            sx={{ fontWeight: 'bold', marginBottom: '10px', marginRight: '10px', textTransform: 'none', height: '47px' }}
                            loadingPosition="start"
                            startIcon={<SearchIcon />}
                            variant="contained"
                            disabled={ !canSearch }
                            onClick={ handleSearchClick }
                            loading={ isLoading }
                        >
                            Search
                        </LoadingButton>
                        
                    </CardActions>

                </Card>

                {
                    ( arrivalLog?.items.length === 0 && isLoaded ) && <Typography sx={{ marginTop: '15px' }}>No arrival log entries to display</Typography>
                }

                {
                    ( arrivalLog && arrivalLog?.items.length > 0 )
                        &&
                    <>
                        <Typography sx={{ color: bhpColor, fontSize: '20px', fontWeight: 'bold', marginTop: '15px' }}>Village Arrival Logs</Typography>
                        
                        <DataGrid
                            autoHeight={ true }
                            columns={ columns }
                            disableSelectionOnClick
                            getRowId={ _ => Math.floor( Math.random( ) * 100_000_000 ) }
                            hideFooterPagination={ true }
                            rows={ arrivalLog.items }
                            sortingOrder={ [ 'desc', 'asc' ]}
                            sx={{ ...dataGridSx, width: 1750, boxShadow: 5, marginTop: '10px !important' }}
                        />

                        <Box
                            alignItems="center"
                            display="flex" 
                            justifyContent="flex-end"
                            width={ 1750 }
                            sx={{ marginTop: '0px !important'}}
                        >
                            <ArrivalLogsPagination totalCount={ arrivalLog.totalCount } />
                        </Box>
                    </>
                }

            </Stack>

        </>

    );

};